<template>
  <v-dialog v-model="visible" max-width="900" persistent>
    <v-card class="vehicle-detail-card">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-car</v-icon>
        <v-toolbar-title>Detalles del Vehículo</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-0">
        <v-container v-if="vehicle" fluid class="pa-0">
          <!-- Banner con información principal -->
          <v-row no-gutters>
            <v-col cols="12">
              <v-sheet color="grey lighten-4" class="pa-5 text-center vehicle-banner">
                <div class="d-flex flex-column flex-md-row align-center justify-space-around">
                  <div>
                    <div class="text-h6 font-weight-bold primary--text">{{ vehicle.brand }}</div>
                    <div class="text-subtitle-1">{{ vehicle.model }} {{ vehicle.version }}</div>
                  </div>
                  <div>
                    <v-avatar color="primary" size="80" class="white--text text-h4 font-weight-bold mb-2">
                      {{ vehicle.brand ? vehicle.brand.charAt(0) : '?' }}
                    </v-avatar>
                  </div>
                  <div>
                    <v-chip :color="getStatusColor(vehicle.status)" dark class="pa-2">
                      <v-icon left>{{ getStatusIcon(vehicle.status) }}</v-icon>
                      {{ getStatusText(vehicle.status) }}
                    </v-chip>
                    <div class="mt-2">
                      <span class="font-weight-medium">Matricula:</span> {{ vehicle.plate_number || 'No asignada' }}
                    </div>
                    <div v-if="isPending" class="mt-2">
                      <v-alert dense type="warning" color="orange" class="caption">
                        Complete todos los datos del vehículo antes de realizar un movimiento
                      </v-alert>
                    </div>
                    <div v-if="isUnregister" class="mt-2">
                      <v-alert dense type="warning" color="orange" class="caption">
                        Debe aceptar el movimiento pendiente antes de realizar uno nuevo
                      </v-alert>
                    </div>
                  </div>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
          <!-- Tabs para organizar la información -->
          <v-tabs v-model="activeTab" background-color="transparent" grow slider-color="primary" class="elevation-1">
            <v-tab>
              <v-icon left>mdi-information-outline</v-icon>
              Características
            </v-tab>
            <v-tab>
              <v-icon left>mdi-map-marker</v-icon>
              Ubicación
            </v-tab>
            <v-tab>
              <v-icon left>mdi-file-document-outline</v-icon>
              Documentación
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab" class="mt-2">
            <!-- Tab de Características -->
            <!-- Tab de Características -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6" md="4" v-for="(field, index) in characteristicsFields" :key="index">
                      <v-card outlined class="info-card">
                        <v-card-text class="d-flex flex-column align-center text-center pa-3">
                          <v-icon size="36" :color="field.color" class="mb-2">{{ field.icon }}</v-icon>
                          <div class="subtitle-2 grey--text text--darken-1">{{ field.label }}</div>

                          <!-- Mostrar campo de edición cuando estamos en modo edición -->
                          <template v-if="isEditing && vehicle?.status === 'created'">
                            <v-text-field v-if="!field.options" v-model="editedVehicle[field.key]" dense class="mt-1"
                              hide-details></v-text-field>
                            <v-select v-else v-model="editedVehicle[field.key]" :items="field.options" dense
                              class="mt-1" hide-details></v-select>
                          </template>

                          <!-- Mostrar valor cuando no estamos editando -->
                          <div v-else class="subtitle-1 font-weight-medium mt-1">
                            {{ getFieldValue(field.key, field.formatter) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <!-- Tab de Ubicación -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row class="location-cards">
                    <v-col cols="12" md="6">
                      <v-card outlined height="100%">
                        <v-card-title class="subtitle-1">
                          <v-icon left color="primary">mdi-map-marker</v-icon>
                          Ubicación Actual
                        </v-card-title>
                        <v-card-text class="d-flex flex-column align-center justify-space-between">
                          <div class="text-center py-4">
                            <v-avatar color="primary lighten-2" size="100" class="elevation-3 mb-4">
                              <v-icon size="50" color="white">mdi-home-variant</v-icon>
                            </v-avatar>
                            <h3 class="title">{{ vehicle.location ? vehicle.location.name : 'No asignada' }}</h3>
                            <p class="subtitle-1 grey--text">{{ vehicle.location ? vehicle.location.address : 'Pendiente de asignación' }}</p>
                          </div>
                          <p v-if="vehicle.location" class="subtitle-2 text-center mt-auto">
                            <v-icon small color="grey">mdi-clock-outline</v-icon>
                            Desde: {{ formatDate(vehicle.location.updated_at) }}
                          </p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-card outlined height="100%">
                        <v-card-title class="subtitle-1">
                          <v-icon left color="blue">mdi-store</v-icon>
                          Concesionario
                        </v-card-title>
                        <v-card-text class="d-flex flex-column align-center justify-space-between">
                          <div class="text-center py-4">
                            <v-avatar color="blue lighten-2" size="100" class="elevation-3 mb-4">
                              <v-icon size="50" color="white">mdi-domain</v-icon>
                            </v-avatar>
                            <h3 class="title">{{ vehicle.dealer ? vehicle.dealer.name : 'No asignado' }}</h3>
                            <p class="subtitle-1 grey--text">{{ vehicle.dealer ? vehicle.dealer.address : 'Pendiente de asignación' }}</p>
                          </div>
                          <p v-if="vehicle.dealer" class="subtitle-2 text-center mt-auto">
                            <v-icon small color="grey">mdi-phone</v-icon>
                            {{ vehicle.dealer.phone || 'Sin teléfono' }}
                          </p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <!-- Tab de Documentación -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-list two-line>
                        <v-subheader class="title">Información General</v-subheader>
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-icon color="primary">mdi-car</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>Número IIC</v-list-item-title>
                            <v-list-item-subtitle>{{ vehicle.iic_number || 'No registrado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-icon color="primary">mdi-numeric</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>Número de Orden</v-list-item-title>
                            <v-list-item-subtitle>{{ vehicle.order_number || 'No registrado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-icon color="primary">mdi-barcode</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>Número de Bastidor</v-list-item-title>
                            <v-list-item-subtitle>{{ vehicle.frame_number || 'No registrado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-list two-line>
                        <v-subheader class="title">Fechas Importantes</v-subheader>
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-icon color="green">mdi-calendar-plus</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>Fecha de Registro</v-list-item-title>
                            <v-list-item-subtitle>{{ formatDate(vehicle.created_at) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-icon color="orange">mdi-calendar-edit</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>Última Actualización</v-list-item-title>
                            <v-list-item-subtitle>{{ formatDate(vehicle.updated_at) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-container>
        <v-container v-else class="text-center pa-5">
          <v-icon size="64" color="grey lighten-1">mdi-car-off</v-icon>
          <p class="title mt-3 grey--text">No se encontraron detalles del vehículo.</p>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="flex-wrap">
        <v-spacer></v-spacer>
        <!-- Botones cuando estamos en modo de edición -->
        <template v-if="isEditing">
          <v-btn color="error" @click="cancelEdit" text class="ma-1" block x-small-only>
        <v-icon left>mdi-close</v-icon>
        Cancelar
          </v-btn>
          <v-btn color="success" @click="saveVehicle" :loading="saving" :disabled="saving" class="ma-1" block x-small-only>
        <v-icon left v-if="!saving">mdi-content-save</v-icon>
        Guardar
          </v-btn>
        </template>
        <!-- Botones cuando no estamos en modo de edición -->
        <template v-else>
          <v-btn color="grey darken-1" text @click="close" class="ma-1" :block="$vuetify.breakpoint.xsOnly">
            <v-icon left>mdi-close</v-icon>
            Cerrar
          </v-btn>
          <v-btn color="primary" @click="aceptar" :disabled="isPending || isUnregister" class="ma-1" :block="$vuetify.breakpoint.xsOnly">
            <v-icon left>mdi-check</v-icon>
            {{ vehicle?.status === 'created' ? 'Crear Recepción' : 'Aceptar' }}
          </v-btn>
          <v-btn v-if="vehicle?.status === 'created'" color="warning" @click="editVehicle" class="ma-1" :block="$vuetify.breakpoint.xsOnly">
            <v-icon left>mdi-pencil</v-icon>
            Editar Vehículo
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
    <transport-form v-if="showTransportForm" :visible="showTransportForm" :movimientoId="transportMovimientoId"
      :status="vehicle" @submit="handleTransportSubmit" @close="handleTransportClose" />
  </v-dialog>
</template>

<script>
import TransportForm from './TransportForm'
export default {
  props: {
    vehicle: Object,
    visible: Boolean,
    statusMob: String
  },
  components: {
    TransportForm
  },
  data: () => ({
    activeTab: 0,
    isEditing: false,
    editedVehicle: {},
    saving: false,
    characteristicsFields: [
      {
        key: 'condition',
        label: 'Condición',
        icon: 'mdi-tag',
        color: 'blue',
        formatter: 'getConditionText',
        options: [
          { text: 'Nuevo', value: 'new' },
          { text: 'Usado', value: 'used' }
        ]
      },
      { key: 'brand', label: 'Marca', icon: 'mdi-car-side', color: 'red' },
      { key: 'model', label: 'Modelo', icon: 'mdi-car-info', color: 'green' },
      { key: 'version', label: 'Versión', icon: 'mdi-alpha-v-circle', color: 'purple' },
      { key: 'color', label: 'Color', icon: 'mdi-palette', color: 'orange' },
      {
        key: 'fuel',
        label: 'Combustible',
        icon: 'mdi-gas-station',
        color: 'deep-orange'
      },
      {
        key: 'transmission',
        label: 'Transmisión',
        icon: 'mdi-car-clutch',
        color: 'teal',
        formatter: 'getTransmissionText',
        options: [
          { text: 'Automática', value: 'auto' },
          { text: 'Manual', value: 'manual' }
        ]
      },
      { key: 'engine', label: 'Motor', icon: 'mdi-engine', color: 'brown' },
      { key: 'plate_number', label: 'Matrícula', icon: 'mdi-card-text', color: 'indigo' }
    ],
    showTransportForm: false,
    transportMovimientoId: null,
    transportStatus: {}
  }),
  methods: {
    close () {
      this.$emit('close')
    },
    aceptar () {
      if (this.vehicle.status === 'created') {
        // Mostrar el formulario de transporte directamente
        this.showTransportForm = true
        // Close the vehicle detail dialog when transport form opens
        this.$emit('close')
        // Crear un objeto de estado inicial para el formulario de transporte
        this.transportStatus = {
          status: 'created', // Estado inicial para un nuevo transporte
          vehicle_id: this.vehicle.id,
          source_location_id: this.vehicle.location_id || null,
          movement_id: false
          // Otros campos necesarios
        }
        // Crearemos un ID temporal para el movimiento
        this.transportMovimientoId = this.vehicle.id || 0
      } else {
        this.$emit('accept')
      }
    },
    handleTransportSubmit (data) {
      this.showTransportForm = false
      // Actualizar el estado del vehículo a "registered" después de crear el transporte
      this.updateVehicleStatus('registered')
      // Emitir evento para notificar que se ha añadido un movimiento
      this.$emit('movement-added', data)
      this.$emit('accept')
    },

    handleTransportClose () {
      this.showTransportForm = false
    },
    getTransmissionText (transmission) {
      const options = {
        'auto': 'Automática',
        'manual': 'Manual'
      }
      return options[transmission] || transmission || 'No especificada'
    },
    getConditionText (condition) {
      const options = {
        'new': 'Nuevo',
        'used': 'Usado'
      }
      return options[condition] || condition || 'No especificada'
    },
    getStatusText (status) {
      const options = {
        'pending': 'Pendiente',
        'created': 'Creado',
        'registered': 'Registrado',
        'delivered': 'Entregado'
      }
      return options[status] || status || 'No especificado'
    },
    getStatusColor (status) {
      const colors = {
        'pending': 'orange',
        'created': 'blue',
        'registered': 'green',
        'delivered': 'purple'
      }
      return colors[status] || 'grey'
    },
    getStatusIcon (status) {
      const icons = {
        'pending': 'mdi-timer-sand',
        'created': 'mdi-creation',
        'registered': 'mdi-clipboard-check',
        'delivered': 'mdi-truck-delivery'
      }
      return icons[status] || 'mdi-help-circle'
    },
    formatDate (timestamp) {
      if (!timestamp) return 'No disponible'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getFieldValue (key, formatter) {
      if (!this.vehicle) return 'No disponible'
      const value = this.vehicle[key]
      if (formatter && this[formatter]) {
        return this[formatter](value)
      }
      return value || 'No especificado'
    },
    editVehicle () {
      // Iniciar modo de edición
      this.isEditing = true
      // Clonar el vehículo para edición
      this.editedVehicle = JSON.parse(JSON.stringify(this.vehicle))
    },

    cancelEdit () {
      // Salir del modo de edición sin guardar
      this.isEditing = false
      this.editedVehicle = {}
    },

    saveVehicle () {
      // Prepare vehicle data
      this.saving = true
      const vehiculo = this.editedVehicle

      // Call store action to save changes
      this.$store.dispatch('UPDATE_VEHICULOS', vehiculo).then(response => {
        let { error, data, status } = response
        if (error) {
          if (status === 422) {
            // Handle validation errors
            if (data.data && data.data.name && data.data.message &&
                data.data.message.includes('Has superado el máximo de vehículos permitidos')) {
              this.$toast.error(data.data.message, {
                duration: 6000,
                position: 'top-center'
              })
              // Close form after showing error
              setTimeout(() => {
                this.cancelEdit()
              }, 1000)
            } else if (Array.isArray(data)) {
              // Process field validation errors
              data.forEach(({ field, message }) => {
                if (this.errors && this.errors[field]) {
                  this.errors[field].push(message)
                }
              })
            }
          }
        } else {
          // Update was successful
          this.$emit('update:vehicle', data.data)
          this.isEditing = false
          this.$store.dispatch('showMessage', {
            text: 'Vehículo actualizado correctamente',
            color: 'success'
          })
        }
      })
        .finally(() => {
          this.saving = false
        })
    }
  },
  computed: {
    lastMovement () {
      if (this.vehicle.movements) {
        const sortedMovements = [...this.vehicle.movements].sort((a, b) =>
          new Date(b.datetime) - new Date(a.datetime)
        )
        return sortedMovements[0] || {}
      }
      return {}
    },
    isPending () {
      return this.vehicle?.status === 'pending'
    },
    isUnregister () {
      if (!this.vehicle?.movements?.length) return false
      const sortedMovements = [...this.vehicle.movements].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      return sortedMovements[0].status === 'unreported'
    }
  }
}
</script>

<style scoped>
.vehicle-detail-card {
  border-radius: 8px;
  overflow: hidden;
}

.vehicle-banner {
  background: linear-gradient(to right, rgba(66, 165, 245, 0.1), rgba(66, 165, 245, 0.2));
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-card {
  transition: all 0.3s ease;
  border-radius: 8px;
  height: 100%;
}

.info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.v-tabs {
  margin-top: 1px;
}

.v-tab {
  font-weight: 500;
}

.v-list-item {
  border-radius: 8px;
  margin-bottom: 8px;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.location-cards {
  align-items: stretch;
}

.location-cards .v-card {
  display: flex;
  flex-direction: column;
}

.location-cards .v-card__text {
  flex: 1;
}
</style>
